import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import SwitcherIcon from '@atlaskit/icon/core/migration/boards--switcher';
import { Header, useShouldNestedElementRender } from '@atlaskit/side-navigation';
import createBoardLegacy from '@atlassian/jira-create-board-legacy/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import ScopeSwitcherV2 from '@atlassian/jira-navigation-apps-scope-switcher-v2/src/index.tsx';
import type { OnBoardClick } from '@atlassian/jira-navigation-apps-scope-switcher-v2/src/model/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useBoard } from '../../controllers/board/index.tsx';
import { messages } from './messages.tsx';

const ScopeSwitcher = () => {
	const baseUrl = '';
	const { push } = useRouterActions();
	const { data: board } = useBoard();
	const [{ data: contextData }] = useContainerContext();
	const {
		data: { user: currentUser },
	} = useCurrentUser();

	const currentBoard = useMemo(
		() =>
			board != null
				? // URL not need as navigation handled via imperative API using board ID
					{ id: String(board.id), name: board.name, url: '' }
				: // make switcher appear also on boardless projects
					{ id: '0', name: ' ', url: '' },
		[board],
	);

	let locationName = '';
	if (contextData?.contextType === 'project' && contextData.project.name) {
		locationName = contextData.project.name;
	} else if (contextData?.contextType === 'user' && contextData.user.name) {
		locationName = contextData.user.name;
	}

	const onBoardClick: OnBoardClick = useCallback(
		async (clickedBoard) => {
			push(clickedBoard.url);
		},
		[push],
	);

	const onCreateBoard = useCallback(() => {
		createBoardLegacy();
	}, []);

	const projectKey = contextData?.contextType === 'project' ? contextData.project.key : '';

	return (
		<ScopeSwitcherV2
			key={projectKey}
			baseUrl={baseUrl}
			// @ts-expect-error - TS2322 - Type '{ baseUrl: string; boardsInLocation: { id: string; name: string; url: string; }[]; currentBoard: { id: string; name: string; url: string; }; currentProjectKey: string; currentUserName: string; defaultOpen: false; ... 7 more ...; onNavigateUrl: (path: string, state?: any) => void | Promise<...>; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<ScopeSwitcherApp> & Pick<Readonly<Props> & Readonly<...>, "children" | ... 3 more ... | "onBoardClick"> & Partial<...> & Partial<...>'.
			boardsInLocation={[currentBoard]}
			currentBoard={currentBoard}
			currentProjectKey={projectKey}
			currentUserName={currentUser.accountId ?? ''}
			defaultOpen={false}
			isCreateBoardEnabled={!currentUser.isAnonymous}
			isLocationUser={false}
			isSearchEnabled
			locale={currentUser.locale}
			locationName={locationName}
			onCreateBoard={onCreateBoard}
			onBoardClick={onBoardClick}
			onNavigateUrl={push}
		/>
	);
};

const ScopeSwitcherWithAuth = () => {
	const isAnonymous = useIsAnonymous();
	const { data: board } = useBoard();
	const { formatMessage } = useIntl();
	const { shouldRender } = useShouldNestedElementRender();

	if (!shouldRender) {
		return null;
	}

	if (isAnonymous) {
		return (
			<CssFixContainer>
				<Header
					description={formatMessage(messages.board)}
					iconBefore={<SwitcherIcon spacing="spacious" label="" />}
				>
					{board?.name ?? ''}
				</Header>
			</CssFixContainer>
		);
	}

	return <ScopeSwitcher />;
};

export { ScopeSwitcherWithAuth as ScopeSwitcher };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CssFixContainer = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-item-title]': {
		/* fixes CSS leaking from the monolith */
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 'inherit',
	},
});
